import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../api/api.service';
import { getCookie } from '../../utils/cookieUtils';

// Interfaces
export interface Photo {
    id: number;
    url: string;
    language: string;
    position: number;
}

interface BeforeAfterState {
    photos: Photo[];
    isLoading: boolean;
    openReplaceDialog: boolean;
    replacePosition: number | null;
    showDeleteConfirm: boolean;
    photoToDelete: number | null;
    draggedPhoto: Photo | null;
    error: string | null;
}

// Initial state
const initialState: BeforeAfterState = {
    photos: [],
    isLoading: false,
    openReplaceDialog: false,
    replacePosition: null,
    showDeleteConfirm: false,
    photoToDelete: null,
    draggedPhoto: null,
    error: null,
};

// Async thunks
export const fetchPhotos = createAsyncThunk(
    'beforeAfter/fetchPhotos',
    async (_, { rejectWithValue }) => {
        try {
            const language = getCookie('i18next') || 'ru';
            const data = await apiService.getProgressPhotos();
            return data
                .filter(photo => photo.language.toUpperCase() === language.toUpperCase())
                .sort((a, b) => a.id - b.id) // Сортируем по ID для стабильного порядка
                .map((photo, index) => ({ ...photo, position: index }));
        } catch (error) {
            return rejectWithValue('Failed to fetch photos');
        }
    }
);

export const updatePhoto = createAsyncThunk(
    'beforeAfter/updatePhoto',
    async ({ photoId, formData }: { photoId: number, formData: FormData }, { rejectWithValue }) => {
        try {
            const response = await apiService.updateProgressPhoto(photoId, formData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to update photo');
        }
    }
);

export const uploadPhoto = createAsyncThunk(
    'beforeAfter/uploadPhoto',
    async (formData: FormData, { rejectWithValue }) => {
        try {
            const response = await apiService.uploadProgressPhoto(formData);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to upload photo');
        }
    }
);

export const deletePhoto = createAsyncThunk(
    'beforeAfter/deletePhoto',
    async (photoId: number, { rejectWithValue }) => {
        try {
            await apiService.deleteProgressPhoto(photoId);
            return photoId;
        } catch (error) {
            return rejectWithValue('Failed to delete photo');
        }
    }
);

// Slice
const beforeAfterSlice = createSlice({
    name: 'beforeAfter',
    initialState,
    reducers: {
        setReplaceDialog: (state, action: PayloadAction<{ open: boolean, position?: number | null }>) => {
            state.openReplaceDialog = action.payload.open;
            if (action.payload.position !== undefined) {
                state.replacePosition = action.payload.position;
            }
        },
        setDeleteConfirm: (state, action: PayloadAction<{ show: boolean, photoId?: number | null }>) => {
            state.showDeleteConfirm = action.payload.show;
            if (action.payload.photoId !== undefined) {
                state.photoToDelete = action.payload.photoId;
            }
        },
        setDraggedPhoto: (state, action: PayloadAction<Photo | null>) => {
            state.draggedPhoto = action.payload;
        },
        swapPhotos: (state, action: PayloadAction<{ targetPosition: number }>) => {
            if (!state.draggedPhoto) return;

            const sourcePhoto = { ...state.draggedPhoto };
            const targetPhoto = state.photos.find(p => p.position === action.payload.targetPosition);

            if (targetPhoto) {
                // Swap positions
                const sourceIndex = state.photos.findIndex(p => p.id === sourcePhoto.id);
                const targetIndex = state.photos.findIndex(p => p.id === targetPhoto.id);

                state.photos[sourceIndex] = { ...targetPhoto, position: sourcePhoto.position };
                state.photos[targetIndex] = { ...sourcePhoto, position: action.payload.targetPosition };
            }

            state.draggedPhoto = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch photos
            .addCase(fetchPhotos.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPhotos.fulfilled, (state, action) => {
                state.isLoading = false;
                state.photos = action.payload;
            })
            .addCase(fetchPhotos.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update photo
            .addCase(updatePhoto.fulfilled, (state, action) => {
                const index = state.photos.findIndex(p => p.id === action.payload.id);

                if (index !== -1) {
                    // Обновляем фото, сохраняя позицию
                    const currentPosition = state.photos[index].position;
                    state.photos[index] = {
                        ...action.payload,
                        position: currentPosition
                    };
                }
            })
            // Upload photo
            .addCase(uploadPhoto.fulfilled, (state, action) => {
                const targetPosition = state.replacePosition || 0;
                const newPhoto = { ...action.payload, position: targetPosition };

                // Remove existing photo at this position if any
                state.photos = state.photos.filter(p => p.position !== targetPosition);

                // Add new photo and sort by ID to maintain stable order
                state.photos.push(newPhoto);
                state.photos.sort((a, b) => a.id - b.id);

                // Reassign positions based on sorted order, but preserve the target position for new photo
                state.photos = state.photos.map((photo, index) => {
                    // If this is the new photo, keep its target position
                    if (photo.id === newPhoto.id) {
                        return { ...photo, position: targetPosition };
                    }
                    // For other photos, assign positions sequentially but skip the target position
                    const adjustedIndex = index < targetPosition ? index : index + 1;
                    return { ...photo, position: adjustedIndex };
                });

                // Sort by position to ensure correct order
                state.photos.sort((a, b) => a.position - b.position);
            })
            // Delete photo
            .addCase(deletePhoto.fulfilled, (state, action) => {
                // Remove the photo
                state.photos = state.photos.filter(photo => photo.id !== action.payload);

                // Sort by ID and reassign positions sequentially
                state.photos.sort((a, b) => a.id - b.id);
                state.photos = state.photos.map((photo, index) => ({ ...photo, position: index }));
            });
    },
});

export const {
    setReplaceDialog,
    setDeleteConfirm,
    setDraggedPhoto,
    swapPhotos
} = beforeAfterSlice.actions;

export default beforeAfterSlice.reducer; 